/**
 * Pulls the player's autosaves out of localStorage so a report can be replayed
 * against the state the bug actually happened in.
 *
 * Only the two autosave keys are read, by name. Nothing else in localStorage
 * (settings, replays, anything a future feature stores there) is touched.
 */

import { AutosaveName, MultiplayerAutosaveName } from '../../../shared/constants';

/** A save this big is corrupt or useless; don't blow the upload limit on it. */
const MaxSaveBytes = 8 * 1024 * 1024;

type CollectedSave = {
	/** File name to use inside the report archive. */
	fileName: string;
	contents: string;
};

type SaveCollection = {
	saves: CollectedSave[];
	/** Keyed by save name, for saves we found but deliberately left out. */
	skipped: Record<string, string>;
};

const SaveNames = [AutosaveName, MultiplayerAutosaveName];

export function collectGameSaves(): SaveCollection {
	const saves: CollectedSave[] = [];
	const skipped: Record<string, string> = {};

	for (const name of SaveNames) {
		let contents: string | null = null;
		try {
			contents = localStorage.getItem(name);
		} catch (e: any) {
			// Storage can be unavailable outright (privacy modes, disabled cookies)
			skipped[name] = `could not read localStorage: ${e?.message ?? e}`;
			continue;
		}

		if (contents === null) continue; // no save of this type, not a problem
		if (contents.length === 0) {
			skipped[name] = 'present but empty';
			continue;
		}
		if (contents.length > MaxSaveBytes) {
			skipped[name] = `too large to attach (${contents.length} bytes)`;
			continue;
		}

		saves.push({ fileName: `saves/${name}.json`, contents });
	}

	return { saves, skipped };
}
